import { useCallback, useEffect, useState } from 'react';
import { Button, Container, Divider, Grid, Paper, Stack, Typography, Box ,  TextField as MuiTextField,} from '@mui/material';
import { useForm } from 'react-hook-form';
import { FormProvider, Table, TextField, SelectTag } from '../../../../../../src/components';
import { dropdownFetch, isToday, request, toast } from '../../../../../../src/utils';
import SearchIcon from '@mui/icons-material/Search';
import { dateFormatter } from '../../../../../../src/utils';

const calculateTotalBillAmount = (indoorServices = []) => {
  const totalFromIndoorServices = indoorServices.reduce((total, item) => total + parseFloat(item.total_amt || 0), 0);
  return parseFloat(totalFromIndoorServices);
};

const calculateDiscAmount = (indoorServices = []) => {
  const totalFromIndoorServicesDisc = indoorServices.reduce((total, item) => total + parseFloat(item.disc_amt || 0), 0);
  return parseFloat(totalFromIndoorServicesDisc);
};

const calculateAddedDiscAmount = (indoorServices = []) => {
  const totalFromIndoorServicesDiscAdded = indoorServices.reduce(
    (total, item) => total + parseFloat(item.add_discount || 0),
    0
  );
  return parseFloat(totalFromIndoorServicesDiscAdded);
};

const calculateTotalReceivedAmount = (indoor_services = [], receipt_voucher = []) => {
  const totalIndoorServicesReceived = indoor_services.reduce(
    (total, service) => total + parseFloat(service.total_recpt || 0),
    0
  );
  const totalReceiptVoucherReceived = receipt_voucher.reduce(
    (total, voucher) => total + parseFloat(voucher.received_amount || 0),
    0
  );
  const totalReceivedAmount = parseFloat(totalIndoorServicesReceived + totalReceiptVoucherReceived).toFixed(2);
  return totalReceivedAmount;
};

const calculateTotalRefundAmount = (refund_voucher = []) => {
  return refund_voucher.reduce((total, item) => total + parseFloat(item.amount || 0), 0).toFixed(2);
};

const calculateTotalRoomCharges = (roomChargeTracker = []) => {
  if (!Array.isArray(roomChargeTracker)) {
    return 0;
  }
  const totalFromRoomCharge = roomChargeTracker.reduce((total, item) => total + parseFloat(item.net_amt || 0), 0);
  return parseFloat(totalFromRoomCharge);
};

const calculateBalance = (indoorServices = [], receipt_voucher = [], refund_voucher = [], roomChargeTracker = []) => {
  // Calculate all the values
  const totalBillAmount = parseFloat(calculateTotalBillAmount(indoorServices));
  const totalDiscoundIndoorService = parseFloat(calculateDiscAmount(indoorServices));
  const totalReceivedAmount = parseFloat(calculateTotalReceivedAmount(indoorServices, receipt_voucher));
  const totalRefundAmount = parseFloat(calculateTotalRefundAmount(refund_voucher));
  const totalRoomCharge = parseFloat(calculateTotalRoomCharges(roomChargeTracker));
  const addedDisAmount = parseFloat(calculateAddedDiscAmount(indoorServices));

  const balance =
    totalBillAmount +
    totalRoomCharge +
    totalRefundAmount -
    (totalReceivedAmount + totalDiscoundIndoorService + addedDisAmount);
  return balance.toFixed(2);
};

function formatDate(dateString) {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}
const DETAIL_HEADER = [
  {
    header: 'Patient Name',
    accessor: (row) => `${row.patient.name_suffix} ${row.patient.first_name} ${row.patient.last_name ? row.patient.last_name: "" }`
  },
  {
    header: 'UHID No',
    accessor: 'uhid'
  },
  {
    header: 'Adm No.',
    accessor: 'ipd_no'
  },
  {
    header: 'Doctor Name',
    accessor: 'consultant_detail.doctor.name'
  },
  {
    header: 'Sponsor',
    accessor: 'patient.sponsor.name'
  },
  {
    header: 'Ward',
    accessor: 'consultant_detail.room.ward.name'
  },
  {
    header: 'Room No.',
    accessor: 'consultant_detail.room.room_no'
  },
  {
    header: 'Bed',
    accessor: 'consultant_detail.bed_no'
  },
  {
    header: 'Adm Date',
    accessor: (row) => formatDate(row?.date)
  },
  {
    header: 'Nur Station',
    accessor: 'consultant_detail.room.nurse_station.name'
  },
  {
    header: 'Bill Amt.',
    accessor: (row) => {
      const totalBillAmount = parseFloat(calculateTotalBillAmount(row.indoor_services)).toFixed(2);
      return totalBillAmount; // Use the total room charge as part of the calculation
    }
  },
  {
    header: 'Room Charges',
    accessor: (row) => parseFloat(calculateTotalRoomCharges(row.roomChargeTracker)).toFixed(2)
  },
  {
    header: 'Rec Amt.',
    accessor: (row) => parseFloat(calculateTotalReceivedAmount(row.indoor_services, row.receipt_voucher)).toFixed(2)
  },
  {
    header: 'Refund Amt.',
    accessor: (row) => parseFloat(calculateTotalRefundAmount(row.refund_voucher)).toFixed(2)
  },
  {
    header: 'Balance',
    accessor: (row) => {
      const balance =
        parseFloat(
          calculateBalance(row.indoor_services, row.receipt_voucher, row.refund_voucher, row.roomChargeTracker)
        ) || 0;
      return balance;
    }
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  }
];

const TODAY_IPD_DETAIL_HEADER = [
  {
    header: 'Patient Name',
    accessor: (row) => `${row.patient.name_suffix} ${row.patient.first_name} ${row.patient.last_name}`
  },
  {
    header: 'UHID No',
    accessor: 'uhid'
  },
  {
    header: 'Ipd No.',
    accessor: 'ipd_no'
  },
  {
    header: 'Consultant',
    accessor: (row) => row?.doctor?.name
  },
  {
    header: 'sponsor',
    accessor: 'patient.sponsor.name'
  }
];

const TODAY_DISCHARGE_DETAIL_HEADER = [
  {
    header: 'Patient Name',
    accessor: (row) => `${row?.patient?.name_suffix}${row?.patient?.first_name} ${row?.patient?.last_name}`
  },
  {
    header: 'UHID No',
    accessor: 'uhid'
  },
  {
    header: 'Adm No.',
    accessor: 'ipd_no'
  },
  {
    header: 'Consultant',
    accessor: (row) => row?.doctor?.name
  },
  {
    header: 'Add Doctor',
    accessor: (row) => row.consultant_detail?.jr_res_doc?.[0]?.name || ''
  },
  {
    header: 'sponsor',
    accessor: 'patient.sponsor.name'
  }
];

const initialIndoorStatusDataObj = {
  data: [],
  loading: false
};

export const IndoorStatus = () => {
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [todayIpdList, setTodayIpdList] = useState([]);
  const [todayDischargedList, setTodayDischargedList] = useState([]);
  const [currentAction, setCurrentAction] = useState();
  const [input,setInput] = useState("")
  const[doctorDropdown,setDoctorDropdown] = useState([])

  const methods = useForm({
    defaultValues: {
 
    },
    mode: 'all'
  });
  const { handleSubmit, setValue, reset, watch } = methods;

  const searchIPDDetails = async () => {
    setLoading(true);
    const query = {
      name : "admit"
    };
    const response = await request('/indoor-registeration-status', {
      method: 'GET',
      query:query,
    });
    if (response?.success) {
      if (!response?.data || response.data.length === 0) {
        toast('No IPD found', { variant: 'warning' });
      } else {
        const fromDate = methods.getValues('from_date');
        const toDate = methods.getValues('to_date');
        const filteredData = filterData(response.data, fromDate, toDate);
        const admitFilteredData = filteredData.filter((item) => item.admission_status === 'admit');
        const ActiveData = admitFilteredData.filter((item) => item.status === 'active');
        const sortedData = ActiveData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setDetailList(sortedData);
      }
    } else {
      toast('Failed to load patient details', { variant: 'error' });
    }
    setLoading(false);
  };



  useEffect(()=>{
    searchPatientDetails()
  },[input])

  const searchPatientDetails = async () => {
    const lowercasedInput = input.toLowerCase().trim();
    setLoading(true);
    const query ={}
    if (lowercasedInput) {
      query.patientName = lowercasedInput;
    }
    const response = await request('/indoor-registeration-status', {
      method: 'GET',
      query:query,
    });
    if (response?.success) {
      if (!response?.data) {
        toast('No IPD found', { variant: 'warning' });
      } else {
        const fromDate = methods.getValues('from_date');
        const toDate = methods.getValues('to_date');
        const filteredData = filterData(response.data, fromDate, toDate);
        const admitFilteredData = filteredData.filter((item) => item.admission_status === 'admit');
        const ActiveData = admitFilteredData.filter((item) => item.status === 'active');
        const sortedData = ActiveData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setDetailList(sortedData);
      }
    } else {
      toast('Failed to load patient details', { variant: 'error' });
    }
    setLoading(false);
  };

const handlePatientChange=(e)=>{
 setInput(e.target.value)
}

  const filterData = (data, fromDate, toDate) => {
    let filteredData = [...data];
    if (fromDate && toDate) {
      const from = new Date(fromDate).setHours(0, 0, 0, 0);
      const to = new Date(toDate).setHours(23, 59, 59, 999);
      filteredData = filteredData.filter((item) => {
        const createdAt = new Date(item.createdAt).getTime();
        return createdAt >= from && createdAt <= to;
      });
    }
    return filteredData;
  };

  useEffect(() => {
    searchIPDDetails();
  }, [methods.watch('from_date'), methods.watch('to_date')]);

  const fetchTodayDischarged = async () => {
    setLoading(true);
    const response = await request('/indoor-registeration-today-discharge-list', {
      method: 'GET'
    });
    if (response?.success) {
      if (!response?.data) {
        toast('No IPD found', { variant: 'warning' });
      } else {
        setTodayDischargedList(response.data);
      }
    } else {
      toast('Failed to load patient details', { variant: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTodayDischarged();
  }, []);

  const fetchTodayAdmit = async () => {
    setLoading(true);
    const response = await request('/indoor-registeration-today-admit-list', {
      method: 'GET'
    });
    if (response?.success) {
      if (!response?.data) {
        toast('No IPD found', { variant: 'warning' });
      } else {
        setTodayIpdList(response.data);
      }
    } else {
      toast('Failed to load patient details', { variant: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTodayAdmit();
  }, []);


  const getDoctorDropdown = useCallback(async () => {
      const data = await dropdownFetch('doctor');
      setDoctorDropdown(data);
    }, []);
  

    useEffect(() => {
      getDoctorDropdown();
    }, [
      getDoctorDropdown,
    ]);
  
const doctorWatch = watch('doctor')

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: '100vw',
            boxSizing: 'border-box',
            minWidth: '100%'
          }}
        >
          <FormProvider methods={methods}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                IPD Status
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '100%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>From Date </Typography>
                    <TextField
                      // disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{ flex: 0.8 }}
                      placeholder="Enter Discount Name"
                      name="from_date"
                      type="date"
                    />
                  </Stack>
                </Grid>
                <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>To Date</Typography>
                    <TextField
                      // disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{ flex: 0.8 }}
                      name="to_date"
                      type="date"
                      placeholder="Select Doctor"
                    />
                  </Stack>
                </Grid>
                <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Doctor</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="doctor"
                      menus={doctorDropdown}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0.5 }}>
                            <MuiTextField
                              sx={{
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                  padding: '0',
                                  fontSize: '1.4rem' // Adjust input font size
                                },
                                '& .MuiOutlinedInput-input': {
                                  padding: '8px 1.4rem'
                                },
                                '& .MuiInputLabel-root': {
                                  fontSize: '1.2rem' // Adjust label font size
                                }
                              }}
                              label="Search Patient By Name,UHID or IPD No"
                              placeholder="Search Patient"
                              variant="outlined"
                              value={input}
                              onChange={handlePatientChange}
                            />
                          </Stack>
                  <Table
                    containerSX={{ flex: 1, border: '0px solid rgba(0,0,0,0.27)' }}
                    data={detailList}
                    columns={DETAIL_HEADER}
                    size="small"
                  />
                </Grid>

                <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.4rem' }}>Today IPD Details</Typography>
                  <Divider />
                  <Table
                    containerSX={{ flex: 1, border: '0px solid rgba(0,0,0,0.27)' }}
                    data={todayIpdList || []}
                    columns={TODAY_IPD_DETAIL_HEADER}
                    size="small"
                  />
                </Grid>

                <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.4rem' }}>Today Discharged Details</Typography>
                  <Divider />
                  <Table
                    containerSX={{ flex: 1, border: '0px solid rgba(0,0,0,0.27)' }}
                    data={todayDischargedList || []}
                    columns={TODAY_DISCHARGE_DETAIL_HEADER}
                  />
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
    </>
  );
};
